import {
  ChevronDown,
  ExternalLink,
  MessageSquareText,
  Minus,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import * as m from "@/paraglide/messages";
import {cn} from "@/lib/utils";
import {normalizeSentiment} from "@/lib/sentiment-utils";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/components/ui/collapsible";
import type {SupportingMention} from "@/lib/personas-api";

interface SupportingMentionsProps {
  mentions: SupportingMention[];
  className?: string;
}

/**
 * Get sentiment display info
 */
function getSentimentInfo(sentiment: string | null | undefined): {
  label: string;
  icon: typeof ThumbsUp;
  className: string;
} {
  switch (normalizeSentiment(sentiment)) {
    case "positive":
      return {
        label: m.mention_positive(),
        icon: ThumbsUp,
        className: "text-green-500",
      };
    case "negative":
      return {
        label: m.mention_negative(),
        icon: ThumbsDown,
        className: "text-red-500",
      };
    default:
      return {
        label: m.mention_neutral(),
        icon: Minus,
        className: "text-muted-foreground",
      };
  }
}

/**
 * Format date for display
 */
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

/**
 * Collapsible section that shows supporting mentions for a persona chat response.
 * Matches the visual style of SourcesSection for UI consistency.
 */
export function SupportingMentions({mentions, className}: SupportingMentionsProps) {
  if (!mentions || mentions.length === 0) {
    return null;
  }

  return (
    <Collapsible defaultOpen={false} className={cn("mt-4", className)}>
      <CollapsibleTrigger className="group -mx-2 flex !cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors select-none hover:bg-zinc-100 dark:hover:bg-zinc-800/50">
        <div className="flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/10 to-purple-500/10 dark:from-blue-500/20 dark:to-purple-500/20">
          <MessageSquareText className="h-3 w-3 text-blue-600 dark:text-blue-400" />
        </div>
        <span className="font-medium text-zinc-700 dark:text-zinc-300">
          {m.persona_chat_sources_label()}
        </span>
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-zinc-200 px-1.5 text-xs font-medium text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300">
          {mentions.length}
        </span>
        <ChevronDown className="ml-auto h-4 w-4 text-zinc-400 transition-transform duration-200 group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-3 flex flex-col gap-3">
          {mentions.map((mention) => (
            <MentionCard key={mention.id} mention={mention} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

interface MentionCardProps {
  mention: SupportingMention;
}

function MentionCard({mention}: MentionCardProps) {
  const sentiment = getSentimentInfo(mention.sentiment);
  const SentimentIcon = sentiment.icon;

  return (
    <div className="bg-muted/50 border-border/50 overflow-hidden rounded-lg border p-3">
      {/* Header: Author, Channel, Date */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm">
          {mention.author && <span className="font-medium">{mention.author}</span>}
          {mention.channel_name && (
            <Badge variant="outline" className="text-xs">
              {mention.channel_name}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <SentimentIcon className={cn("h-3.5 w-3.5", sentiment.className)} />
          {mention.date && (
            <span className="text-muted-foreground text-xs">{formatDate(mention.date)}</span>
          )}
        </div>
      </div>

      {/* Text content */}
      {mention.text && (
        <p className="text-muted-foreground mb-2 line-clamp-3 text-sm [overflow-wrap:anywhere] whitespace-pre-wrap">
          {mention.text}
        </p>
      )}

      {/* Source link */}
      {mention.url && (
        <Button
          variant="ghost"
          size="sm"
          className="text-primary -ml-2 h-auto gap-1 p-2 text-xs"
          asChild
        >
          <a href={mention.url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3 w-3" />
            {m.mention_view_source()}
          </a>
        </Button>
      )}
    </div>
  );
}
